import Link from 'next/link';
import { Info, Repeat, Sparkles } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { ListingBadge, ListingBadgeKind } from '@/lib/listing-badges';

const BADGE_ICONS: Record<ListingBadgeKind, typeof Info> = {
  'resell-rights': Repeat,
  'ai-assisted': Sparkles,
};

/**
 * The labels beside a product's name (src/lib/listing-badges.ts). Each is a
 * button that opens what it means, on a tap as well as a click or Enter: the
 * AI label's meaning used to live in a hover title, which a touch screen never
 * shows. The pill stays small; on a touch screen an invisible margin around it
 * makes a finger-sized target.
 */
export function ListingBadges({ badges }: { badges: readonly ListingBadge[] }) {
  return (
    <>
      {badges.map((badge) => {
        const Icon = BADGE_ICONS[badge.kind];
        return (
          <Popover key={badge.kind}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="relative inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ring-offset-background transition-colors [-webkit-tap-highlight-color:transparent] hover:bg-accent active:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 coarse:after:absolute coarse:after:-inset-x-1 coarse:after:-inset-y-2.5 coarse:after:content-['']"
              >
                <Icon className="h-3 w-3" aria-hidden />
                {badge.label}
                <Info className="h-3 w-3 text-muted-foreground" aria-hidden />
                <span className="sr-only">: what this means</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" collisionPadding={16} className="w-80 max-w-[calc(100vw-2rem)] space-y-2 text-sm">
              <p className="font-medium">{badge.label}</p>
              <p className="text-muted-foreground">{badge.detail}</p>
              <p className="text-muted-foreground">{badge.note}</p>
              {badge.link && (
                <p>
                  <Link href={badge.link.href} className="text-link underline decoration-link/40 underline-offset-4 hover:decoration-link">
                    {badge.link.text}
                  </Link>
                </p>
              )}
            </PopoverContent>
          </Popover>
        );
      })}
    </>
  );
}
